import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const TIPOS_ASIST = ['ASISTIO', 'FALTA', 'TARDANZA', 'PERMISO', 'VACACION', 'FERIADO', 'DESCANSO'];
const TIPO_COLOR = {
  ASISTIO: 'success', FALTA: 'error', TARDANZA: 'warning',
  PERMISO: 'info', VACACION: 'primary', FERIADO: 'secondary', DESCANSO: 'default'
};

const anios = () => { const y = new Date().getFullYear(); return [y, y-1].map(String); };
const meses = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const mesLabel = { '01':'Enero','02':'Febrero','03':'Marzo','04':'Abril','05':'Mayo','06':'Junio','07':'Julio','08':'Agosto','09':'Septiembre','10':'Octubre','11':'Noviembre','12':'Diciembre' };

const EMPTY_FORM = { trabajador_id: '', fecha: '', tipo: 'ASISTIO', hora_entrada: '', hora_salida: '', minutos_tardanza: 0, horas_extra_25: 0, horas_extra_35: 0, observaciones: '' };

export default function Asistencia() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadorId, setTrabajadorId] = useState('');
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [mes, setMes] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) { setTrabajadores([]); return; }
    axios.get('/api/rrhh/trabajadores', { params: { empresa_id: empresaId, estado: 'ACTIVO' } })
      .then(r => setTrabajadores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/asistencia', {
        params: { empresa_id: empresaId, trabajador_id: trabajadorId || undefined, anio, mes }
      });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [empresaId, trabajadorId, anio, mes]);

  useEffect(() => { cargar(); }, [cargar]);

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;
  const trabObj = trabajadores.find(t => t.id === trabajadorId) || null;
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFechaChange = async (fecha) => {
    sf('fecha', fecha);
    if (!fecha) return;
    try {
      const anioFecha = fecha.substring(0, 4);
      const r = await axios.get(`/api/rrhh/feriados?anio=${anioFecha}&empresa_id=${empresaId || ''}`);
      const esFeriado = r.data.some(f => f.fecha?.substring(0, 10) === fecha);
      if (esFeriado) sf('tipo', 'FERIADO');
    } catch (_) { /* silenciar */ }
  };

  const handleGuardar = async () => {
    setError('');
    try {
      await axios.post('/api/rrhh/asistencia', { ...form, empresa_id: empresaId });
      setOpen(false);
      setSuccess('Asistencia registrada.');
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este registro de asistencia?')) return;
    try {
      await axios.delete(`/api/rrhh/asistencia/${id}`);
      setSuccess('Registro eliminado.'); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al eliminar'); }
  };

  // Resumen del mes
  const resumen = rows.reduce((acc, r) => {
    acc[r.tipo] = (acc[r.tipo] || 0) + 1;
    acc.he25 = (acc.he25 || 0) + Number(r.horas_extra_25 || 0);
    acc.he35 = (acc.he35 || 0) + Number(r.horas_extra_35 || 0);
    return acc;
  }, {});

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1300 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>Registro de Asistencia</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ p: 2, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={empresas} getOptionLabel={o => o?.razon_social || ''}
              value={empresaObj}
              onChange={(_, v) => { setEmpresaId(v ? v.empresa_id : ''); setTrabajadorId(''); }}
              isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
              renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <Autocomplete
              options={trabajadores}
              getOptionLabel={o => o ? `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}` : ''}
              value={trabObj}
              onChange={(_, v) => setTrabajadorId(v ? v.id : '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={p => <TextField {...p} label="Trabajador (opcional)" size="small" sx={{ minWidth: '20em' }} />}
              sx={{ minWidth: '20em' }}
            />
            <TextField select label="Año" size="small" value={anio} onChange={e => setAnio(e.target.value)} sx={{ minWidth: '8em' }}>
              {anios().map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
            <TextField select label="Mes" size="small" value={mes} onChange={e => setMes(e.target.value)} sx={{ minWidth: '10em' }}>
              {meses.map(m => <MenuItem key={m} value={m}>{mesLabel[m]}</MenuItem>)}
            </TextField>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ ...EMPTY_FORM, trabajador_id: trabajadorId }); setOpen(true); }} disabled={!empresaId}>
              Registrar Asistencia
            </Button>
          </Box>
        </Paper>

        {/* Resumen */}
        {rows.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {Object.entries(resumen).filter(([k]) => k !== 'he25' && k !== 'he35').map(([tipo, cnt]) => (
              <Chip key={tipo} size="small" label={`${tipo}: ${cnt}`} color={TIPO_COLOR[tipo] || 'default'} />
            ))}
            {resumen.he25 > 0 && <Chip size="small" label={`H.Extra 25%: ${resumen.he25.toFixed(1)}h`} color="primary" variant="outlined" />}
            {resumen.he35 > 0 && <Chip size="small" label={`H.Extra 35%: ${resumen.he35.toFixed(1)}h`} color="secondary" variant="outlined" />}
          </Box>
        )}

        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Trabajador</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Entrada</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Salida</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Min. Tard.</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>H.E. 25%</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>H.E. 35%</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Obs.</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay registros para el período seleccionado</TableCell></TableRow>
                  ) : rows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{r.fecha ? r.fecha.slice(0,10) : ''}</TableCell>
                      <TableCell sx={{ fontSize: '0.85em' }}>{r.trabajador_nombre}</TableCell>
                      <TableCell align="center"><Chip size="small" label={r.tipo} color={TIPO_COLOR[r.tipo] || 'default'} /></TableCell>
                      <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.82em' }}>{r.hora_entrada || '—'}</TableCell>
                      <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.82em' }}>{r.hora_salida || '—'}</TableCell>
                      <TableCell align="center" sx={{ color: r.minutos_tardanza > 0 ? '#f57c00' : 'inherit' }}>{r.minutos_tardanza || 0}</TableCell>
                      <TableCell align="center">{Number(r.horas_extra_25 || 0).toFixed(1)}</TableCell>
                      <TableCell align="center">{Number(r.horas_extra_35 || 0).toFixed(1)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8em', maxWidth: 120 }}>{r.observaciones || ''}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => { setForm({ trabajador_id: r.trabajador_id, fecha: r.fecha?.slice(0,10), tipo: r.tipo, hora_entrada: r.hora_entrada || '', hora_salida: r.hora_salida || '', minutos_tardanza: r.minutos_tardanza || 0, horas_extra_25: r.horas_extra_25 || 0, horas_extra_35: r.horas_extra_35 || 0, observaciones: r.observaciones || '' }); setOpen(true); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleEliminar(r.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">Registrar Asistencia</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              options={trabajadores}
              getOptionLabel={o => o ? `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}` : ''}
              value={trabajadores.find(t => t.id === form.trabajador_id) || null}
              onChange={(_, v) => sf('trabajador_id', v ? v.id : '')}
              renderInput={p => <TextField {...p} label="Trabajador" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <TextField label="Fecha" size="small" type="date" InputLabelProps={{ shrink: true }} value={form.fecha} onChange={e => handleFechaChange(e.target.value)} sx={{ minWidth: '16em' }} />
            <TextField select label="Tipo" size="small" value={form.tipo} onChange={e => sf('tipo', e.target.value)} sx={{ minWidth: '16em' }}>
              {TIPOS_ASIST.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {(form.tipo === 'ASISTIO' || form.tipo === 'TARDANZA') && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Hora Entrada" size="small" type="time" InputLabelProps={{ shrink: true }} value={form.hora_entrada} onChange={e => sf('hora_entrada', e.target.value)} sx={{ minWidth: '10em' }} />
                  <TextField label="Hora Salida" size="small" type="time" InputLabelProps={{ shrink: true }} value={form.hora_salida} onChange={e => sf('hora_salida', e.target.value)} sx={{ minWidth: '10em' }} />
                </Box>
                {form.tipo === 'TARDANZA' && (
                  <TextField label="Minutos de Tardanza" size="small" type="number" value={form.minutos_tardanza} onChange={e => sf('minutos_tardanza', e.target.value)} sx={{ minWidth: '12em' }} />
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="H. Extra 25%" size="small" type="number" value={form.horas_extra_25} onChange={e => sf('horas_extra_25', e.target.value)} sx={{ minWidth: '10em' }} />
                  <TextField label="H. Extra 35%" size="small" type="number" value={form.horas_extra_35} onChange={e => sf('horas_extra_35', e.target.value)} sx={{ minWidth: '10em' }} />
                </Box>
              </>
            )}
            <TextField label="Observaciones" size="small" value={form.observaciones} onChange={e => sf('observaciones', e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar} disabled={!form.trabajador_id || !form.fecha}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
